// ── Design decisions ──
// Vista "de control" del viaje: una tarjeta por paquete (no una tabla) porque
// el operador revisa en tablet y necesita ver de un vistazo QUÉ falta: cliente,
// bulto, tipo de ingreso o artículos por revisar. Cada tarjeta es un botón
// grande (min-h 44) que abre un Drawer lateral con la edición completa del
// paquete, reutilizando AnexoItemRow para que el teclado (Tab, Enter = nueva
// fila) se sienta igual que en Nueva Entrada.
//
// · Los filtros son <select> nativos + un toggle aria-pressed: funcionan con
//   teclado y con dedo sin librerías; la línea de resumen refleja el filtrado.
// · validarCierre (puro, sincrónico) corre en cada render: el pie de página
//   muestra bloqueos en rojo / advertencias en ámbar antes de abrir el modal,
//   y el modal repite la lista completa. Confirmar queda deshabilitado
//   mientras haya bloqueos; las advertencias no impiden cerrar.
// · readOnly (viaje cerrado) muestra un banner y deshabilita los controles del
//   drawer, pero la navegación y los filtros siguen funcionando.
// · El estado del drawer es sólo el id del paquete: los datos siempre salen
//   de `viaje` (controlado por el contenedor), así nunca quedan desfasados.
// · Posición editable (delta AMB-MOBILE): cada tarjeta muestra su PK-NN —el
//   MISMO número que imprime el Anexo: (sort_order ?? 0) + 1, fórmula de
//   buildViajeAnexoPdfInput— y el operador lo cambia ahí mismo. Editar
//   renumera TODO el viaje 0..N-1 (sin duplicados ni huecos, ver
//   shared/ordenPaquetes.js), reordena las tarjetas y persiste por el camino
//   de siempre: cambiarPaquete({sort_order}) → autosave silencioso →
//   autosaveViaje. Sin botón de guardar y sin funciones nuevas de adapter.
//   El control vive FUERA del botón de la tarjeta (un input dentro de un
//   botón es DOM inválido) en una fila de cabecera propia.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, Lock, Plus, Sparkles } from 'lucide-react';
import AnexoItemRow from '../anexo/AnexoItemRow';
import { validarCierre } from '../../api/supabase/viajeroEncomienda';
import ClientePicker from './shared/ClientePicker';
import { BultoSelect, nombreBulto } from './shared/BultoRail';
import TipoIngresoControl from './shared/TipoIngresoControl';
import CatalogHoverPicker from './shared/CatalogHoverPicker';
import IsNewToggle from './shared/IsNewToggle';
import Drawer from './shared/Drawer';
import Modal from './shared/Modal';
import { ProvenanceChip, ReviewDot, SinBultoChip, SinClienteChip, TipoIngresoChip } from './shared/chips';
import { TIPO_INGRESO_OPTIONS, fmtUsd, nombreCliente, paqueteFob } from './shared/labels';
import { posicionPaquete, renumerarPaquetes } from './shared/ordenPaquetes';

const MAX_DESC = 60;

const selectBase =
  'min-h-[44px] rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-900 ' +
  'outline-none focus:border-[var(--brand-teal)] transition-colors';

const pluralArticulos = (n) => (n === 1 ? '1 artículo' : `${n} artículos`);
const pluralPaquetes = (n) => (n === 1 ? '1 paquete' : `${n} paquetes`);

// Misma asignación que Nueva Entrada al ligar/desligar un ítem del catálogo.
const cambiosDeCatalogo = (row) =>
  row
    ? {
        customs_catalog_id: row.id,
        descripcion: String(row.descripcion || '').slice(0, MAX_DESC),
        cip_unitario_usd: row.cip_usd ?? null,
        unidad_medida: row.unidad_medida || 'UNIDAD',
        fob_unitario_usd: Number(row.fob_usd) || 0,
        manual_override: false,
      }
    : { customs_catalog_id: null, cip_unitario_usd: null, manual_override: true };

// ── Tarjeta ──────────────────────────────────────────────────────────────────

// Posición editable del paquete. Muestra (sort_order ?? 0) + 1 — el número del
// PK-NN del Anexo — y confirma con blur o Enter (nunca mientras se teclea: un
// "12" dispararía con el "1"). Escape restaura. El resync por useEffect cubre
// el caso en que OTRO paquete renumeró a este.
const PosicionPaquete = ({ paquete, etiqueta, total, readOnly, onCambiar }) => {
  const pos = posicionPaquete(paquete);
  const [texto, setTexto] = useState(String(pos));
  useEffect(() => setTexto(String(pos)), [pos]);

  const confirmar = () => {
    const n = Math.round(Number(texto));
    if (!Number.isFinite(n) || n === pos) {
      setTexto(String(pos));
      return;
    }
    onCambiar(paquete.id, n);
  };

  return (
    <label
      className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-slate-500"
      title="Posición del paquete en el Anexo (PK-NN)"
    >
      PK-
      <input
        type="number"
        min={1}
        max={total}
        step={1}
        inputMode="numeric"
        value={texto}
        disabled={readOnly}
        aria-label={`Posición del paquete de ${etiqueta}`}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // Solo blur: confirmar acá Y en onBlur persistiría dos veces.
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            setTexto(String(pos));
          }
        }}
        className="min-h-[44px] w-14 rounded-md border border-slate-200 bg-white px-1.5 text-center text-xs font-semibold text-slate-900 outline-none transition-colors focus:border-[var(--brand-teal)] disabled:opacity-50"
      />
    </label>
  );
};

const PaqueteCard = ({ paquete, cliente, bulto, total, readOnly, onOpen, onCambiarPosicion }) => {
  const items = paquete.items || [];
  const requiereRevision = items.some((it) => it.requires_review);
  const tieneIA = items.some((it) => it.extraction_source === 'ai');
  const etiqueta = cliente || 'sin cliente';
  return (
    <li className="list-none" aria-label={`Paquete de ${etiqueta}`}>
      <div className="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-colors focus-within:border-[var(--brand-teal)] hover:border-[var(--brand-teal)]">
        {/* Cabecera propia: la posición es un input y no puede vivir dentro
            del botón de la tarjeta (DOM inválido). */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-1.5">
          <PosicionPaquete
            paquete={paquete}
            etiqueta={etiqueta}
            total={total}
            readOnly={readOnly}
            onCambiar={onCambiarPosicion}
          />
          <div className="flex shrink-0 items-center gap-1.5">
            <ReviewDot visible={requiereRevision} />
            {tieneIA && (
              <span
                className="inline-flex items-center gap-0.5 rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700"
                title="Contiene artículos extraídos por IA"
              >
                <Sparkles size={10} aria-hidden="true" /> IA
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Abrir paquete de ${etiqueta}`}
          className="flex min-h-[44px] w-full flex-col gap-2 p-3 text-left transition-colors hover:bg-slate-50 focus:outline-none"
        >
          <div className="min-w-0">
            {cliente ? (
              <p className="truncate text-sm font-semibold text-slate-900">{cliente}</p>
            ) : (
              <div className="flex items-center gap-2">
                <SinClienteChip />
                {paquete.nombre && (
                  <span className="truncate text-xs text-slate-500" title={paquete.nombre}>
                    {paquete.nombre}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {bulto ? (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                {bulto}
              </span>
            ) : (
              <SinBultoChip />
            )}
            <TipoIngresoChip value={paquete.tipo_ingreso} />
          </div>
          <div className="flex w-full items-center justify-between text-xs text-slate-600">
            <span>{pluralArticulos(items.length)}</span>
            <span className="font-mono font-semibold text-slate-900">FOB ${fmtUsd(paqueteFob(paquete))}</span>
          </div>
        </button>
      </div>
    </li>
  );
};

// ── Drawer de edición ────────────────────────────────────────────────────────

const PaqueteDrawer = ({ paquete, viaje, customers, acciones, api, readOnly, onClose }) => {
  const abierto = Boolean(paquete);
  const cliente = paquete ? nombreCliente(customers, paquete.customer_id) : null;
  const items = paquete?.items || [];
  const searchCatalog = api?.searchCatalog;

  return (
    <Drawer open={abierto} width="lg" title={`Paquete de ${cliente || 'sin cliente'}`} onClose={onClose}>
      {paquete && (
        <div className="flex flex-col gap-4">
          <section className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-col gap-1">
              <label htmlFor={`cliente-${paquete.id}`} className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Cliente
              </label>
              <ClientePicker
                id={`cliente-${paquete.id}`}
                value={paquete.customer_id}
                customers={customers}
                disabled={readOnly}
                onQuickCreate={acciones?.quickCreateCustomer}
                onChange={(customerId, customer) =>
                  acciones?.cambiarPaquete(paquete.id, {
                    customer_id: customerId,
                    nombre: customer?.full_name || paquete.nombre || '',
                  })
                }
              />
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Bulto</span>
                <BultoSelect
                  bultos={viaje.bultos || []}
                  value={paquete.bulto_id}
                  disabled={readOnly}
                  onChange={(bultoId) => acciones?.cambiarPaquete(paquete.id, { bulto_id: bultoId })}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Tipo de ingreso</span>
                <TipoIngresoControl
                  value={paquete.tipo_ingreso}
                  disabled={readOnly}
                  onChange={(tipo) => acciones?.cambiarPaquete(paquete.id, { tipo_ingreso: tipo })}
                />
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Artículos · {pluralArticulos(items.length)}
              </h3>
              <span className="font-mono text-xs font-semibold text-slate-900">FOB ${fmtUsd(paqueteFob(paquete))}</span>
            </div>
            {items.length === 0 && (
              <p className="py-2 text-xs text-slate-500">Este paquete todavía no tiene artículos.</p>
            )}
            <div className={`flex flex-col gap-1 ${readOnly ? 'pointer-events-none opacity-60' : ''}`} aria-disabled={readOnly}>
              {items.map((it) => (
                <div key={it.id} className="border-b border-slate-100 pb-1.5 last:border-b-0">
                  <AnexoItemRow
                    item={it}
                    onChange={(cambios) => !readOnly && acciones?.cambiarItem(it.id, cambios)}
                    onQuitar={() => !readOnly && acciones?.quitarItem(it.id)}
                    onEnterNewRow={() => !readOnly && acciones?.agregarItem(paquete.id)}
                  />
                  <div className="flex flex-wrap items-center gap-2 px-1.5 pt-1">
                    <IsNewToggle
                      value={it.is_new}
                      disabled={readOnly}
                      onChange={(v) => acciones?.cambiarItem(it.id, { is_new: v })}
                    />
                    <ProvenanceChip source={it.extraction_source} />
                    {it.requires_review && (
                      <span className="inline-flex items-center gap-1.5">
                        <ReviewDot visible />
                        <button
                          type="button"
                          disabled={readOnly}
                          onClick={() => acciones?.cambiarItem(it.id, { requires_review: false })}
                          className="rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                        >
                          Marcar revisado
                        </button>
                      </span>
                    )}
                    <span className="ml-auto">
                      <CatalogHoverPicker
                        item={it}
                        searchCatalog={searchCatalog}
                        disabled={readOnly}
                        onLock={(row) => acciones?.cambiarItem(it.id, cambiosDeCatalogo(row))}
                      />
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              disabled={readOnly}
              onClick={() => acciones?.agregarItem(paquete.id)}
              className="mt-2 inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-300 px-3 text-xs font-semibold text-slate-700 hover:border-[var(--brand-teal)] hover:bg-[var(--brand-teal-soft)] disabled:opacity-50"
            >
              <Plus size={14} aria-hidden="true" /> Añadir ítem
            </button>
          </section>
        </div>
      )}
    </Drawer>
  );
};

// ── Pestaña ──────────────────────────────────────────────────────────────────

const ManifiestoTab = ({ viaje, customers = [], acciones, onCerrar, cerrando = false, readOnly = false, api }) => {
  const [filtroBulto, setFiltroBulto] = useState('todos');
  const [filtroCliente, setFiltroCliente] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [soloRevision, setSoloRevision] = useState(false);
  const [paqueteAbiertoId, setPaqueteAbiertoId] = useState(null);
  const [modalCerrar, setModalCerrar] = useState(false);
  const [errorCierre, setErrorCierre] = useState('');

  const bultos = useMemo(() => viaje?.bultos || [], [viaje]);
  const paquetes = useMemo(
    () => [...(viaje?.paquetes || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [viaje]
  );

  const nombreDeBulto = useCallback(
    (bultoId) => {
      const i = bultos.findIndex((b) => b.id === bultoId);
      return i >= 0 ? nombreBulto(bultos[i], i) : null;
    },
    [bultos]
  );

  const clientesEnViaje = useMemo(() => {
    const ids = new Set(paquetes.map((p) => p.customer_id).filter(Boolean));
    return customers.filter((c) => ids.has(c.id));
  }, [paquetes, customers]);

  const filtrados = useMemo(
    () =>
      paquetes.filter((p) => {
        if (filtroBulto === 'sin') {
          if (p.bulto_id) return false;
        } else if (filtroBulto !== 'todos' && p.bulto_id !== filtroBulto) return false;
        if (filtroCliente === 'sin') {
          if (p.customer_id) return false;
        } else if (filtroCliente !== 'todos' && p.customer_id !== filtroCliente) return false;
        if (filtroTipo === 'sin') {
          if (p.tipo_ingreso) return false;
        } else if (filtroTipo !== 'todos' && p.tipo_ingreso !== filtroTipo) return false;
        if (soloRevision && !(p.items || []).some((it) => it.requires_review)) return false;
        return true;
      }),
    [paquetes, filtroBulto, filtroCliente, filtroTipo, soloRevision]
  );

  const totalArticulos = filtrados.reduce((s, p) => s + (p.items || []).length, 0);
  const totalFob = filtrados.reduce((s, p) => s + paqueteFob(p), 0);

  const { bloqueos, advertencias } = useMemo(() => validarCierre(viaje), [viaje]);

  const paqueteAbierto = paqueteAbiertoId ? paquetes.find((p) => p.id === paqueteAbiertoId) || null : null;
  const cerrarDrawer = useCallback(() => setPaqueteAbiertoId(null), []);

  // Renumera TODO el viaje (no solo los filtrados) y persiste cada paquete
  // cuyo sort_order cambió por cambiarPaquete → autosave silencioso. La
  // función pura garantiza 0..N-1 contiguo: sin duplicados ni huecos.
  const cambiarPosicion = useCallback(
    (paqueteId, nuevaPosicion) => {
      const cambios = renumerarPaquetes(viaje?.paquetes || [], paqueteId, nuevaPosicion);
      const actuales = new Map((viaje?.paquetes || []).map((p) => [p.id, p.sort_order ?? 0]));
      cambios.forEach(({ id, sort_order }) => {
        if (actuales.get(id) !== sort_order) acciones?.cambiarPaquete(id, { sort_order });
      });
    },
    [viaje, acciones]
  );

  const abrirModal = () => {
    setErrorCierre('');
    setModalCerrar(true);
  };
  const cerrarModal = useCallback(() => {
    if (cerrando) return;
    setModalCerrar(false);
  }, [cerrando]);

  const confirmarCierre = async () => {
    if (bloqueos.length > 0 || cerrando) return;
    setErrorCierre('');
    try {
      await onCerrar?.();
      setModalCerrar(false);
    } catch (e) {
      setErrorCierre(e?.message || 'No se pudo cerrar el manifiesto.');
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f8fafc]">
      {readOnly && (
        <div
          role="status"
          className="flex items-center gap-2 border-b border-green-200 bg-green-50 px-4 py-2 text-xs font-semibold text-green-800"
        >
          <Lock size={14} aria-hidden="true" /> Manifiesto cerrado — solo lectura.
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {/* Filtros */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <Filter size={12} aria-hidden="true" /> Filtros
          </span>
          <select
            aria-label="Filtrar por bulto"
            value={filtroBulto}
            onChange={(e) => setFiltroBulto(e.target.value)}
            className={selectBase}
          >
            <option value="todos">Todos los bultos</option>
            {bultos.map((b, i) => (
              <option key={b.id} value={b.id}>
                {nombreBulto(b, i)}
              </option>
            ))}
            <option value="sin">Sin bulto</option>
          </select>
          <select
            aria-label="Filtrar por cliente"
            value={filtroCliente}
            onChange={(e) => setFiltroCliente(e.target.value)}
            className={selectBase}
          >
            <option value="todos">Todos los clientes</option>
            {clientesEnViaje.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
              </option>
            ))}
            <option value="sin">Sin cliente</option>
          </select>
          <select
            aria-label="Filtrar por tipo de ingreso"
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className={selectBase}
          >
            <option value="todos">Todos los tipos</option>
            {TIPO_INGRESO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            <option value="sin">Sin tipo</option>
          </select>
          <button
            type="button"
            aria-pressed={soloRevision}
            onClick={() => setSoloRevision((v) => !v)}
            className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-md border px-3 text-xs font-semibold transition-colors ${
              soloRevision
                ? 'border-amber-400 bg-amber-50 text-amber-800'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden="true" />
            Solo con revisión pendiente
          </button>
        </div>

        <p className="mb-3 text-xs text-slate-600" aria-live="polite">
          {pluralPaquetes(filtrados.length)} · {pluralArticulos(totalArticulos)} · FOB ${fmtUsd(totalFob)}
        </p>

        {paquetes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            Este viaje todavía no tiene paquetes. Agregalos desde Nueva Entrada.
          </div>
        ) : filtrados.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            Ningún paquete coincide con los filtros.
          </div>
        ) : (
          <ul aria-label="Paquetes del viaje" className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filtrados.map((p) => (
              <PaqueteCard
                key={p.id}
                paquete={p}
                cliente={nombreCliente(customers, p.customer_id)}
                bulto={nombreDeBulto(p.bulto_id)}
                total={paquetes.length}
                readOnly={readOnly}
                onOpen={() => setPaqueteAbiertoId(p.id)}
                onCambiarPosicion={cambiarPosicion}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Pie fijo */}
      <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white px-4 py-3">
        <div className="text-xs">
          {bloqueos.length > 0 ? (
            <span className="font-semibold text-red-600">
              No se puede cerrar: {bloqueos.length} {bloqueos.length === 1 ? 'motivo' : 'motivos'}
            </span>
          ) : advertencias.length > 0 ? (
            <span className="font-semibold text-amber-700">
              {advertencias.length} {advertencias.length === 1 ? 'advertencia' : 'advertencias'}
            </span>
          ) : (
            <span className="font-semibold text-green-700">Listo para cerrar</span>
          )}
        </div>
        <button
          type="button"
          disabled={readOnly}
          onClick={abrirModal}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Lock size={14} aria-hidden="true" /> Cerrar manifiesto
        </button>
      </div>

      <PaqueteDrawer
        paquete={paqueteAbierto}
        viaje={viaje}
        customers={customers}
        acciones={acciones}
        api={api}
        readOnly={readOnly}
        onClose={cerrarDrawer}
      />

      <Modal
        open={modalCerrar}
        title="Cerrar manifiesto"
        onClose={cerrarModal}
        actions={
          <>
            <button
              type="button"
              onClick={cerrarModal}
              disabled={cerrando}
              className="min-h-[44px] rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={confirmarCierre}
              disabled={bloqueos.length > 0 || cerrando}
              className="min-h-[44px] rounded-md bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {cerrando ? 'Cerrando…' : 'Sí, cerrar manifiesto'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {bloqueos.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-600">
                Bloqueos ({bloqueos.length})
              </p>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-red-700">
                {bloqueos.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}
          {advertencias.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                Advertencias ({advertencias.length})
              </p>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-amber-800">
                {advertencias.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
          )}
          <p>Esta acción cierra el viaje para edición. Podés reabrirlo desde Producción.</p>
          {errorCierre && (
            <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {errorCierre}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ManifiestoTab;
